import clsx from 'clsx';
import { X } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { lazy, Suspense, useCallback, useState } from 'react';
import { Chip } from '@//components/Chip';
import { useMarket, useMarketMetadata } from '@//providers/MarketSettingsProvider';
import type { EnrichedElasticSearchResponseFacet } from '@//utils/vehicles-search/useEnrichedFacets';
import useUpdateVehiclesSearchUrl from '@//utils/vehicles-search/useUpdateVehiclesSearchUrl';
import useVehicleSearchParams from '@//utils/vehicles-search/useVehicleSearchParams';
import { sanitizeParams } from '@//utils/vehicles-search/utils';
import { getTranslatedInventoryStatus } from '../inventoryStatusUtils';
import styles from './ActiveFilters.module.scss';
import useActiveFacets from './useActiveFacets';
import { getLocationActiveFacetLabel, getRangeActiveFacetLabel } from './utils';

const FilterModal = lazy(() => import('../FilterModals/FilterModal'));

interface ActiveFiltersProps {
  q?: string;
  enrichedFacets?: EnrichedElasticSearchResponseFacet[];
  loading: boolean;
  rootUrl: string;
  totalHits?: number;
}

const ActiveFilters = ({ q, enrichedFacets, loading, rootUrl, totalHits }: ActiveFiltersProps) => {
  const t = useTranslations('utils.search');
  const tInventory = useTranslations('utils.vehicleGlossary.inventoryStatus.values');
  const market = useMarket();
  const marketMetadata = useMarketMetadata();
  const locale = useLocale();
  const updateUrl = useUpdateVehiclesSearchUrl(rootUrl);
  const searchParams = useVehicleSearchParams(rootUrl);
  const [selectedFacetId, setSelectedFacetId] = useState<string>();
  const facet = enrichedFacets?.find((f) => f.id === selectedFacetId);

  const onUpdateActiveFilters = useCallback(
    (params: URLSearchParams) => {
      updateUrl(sanitizeParams(params));
    },
    [updateUrl],
  );

  // Build list of active facets: includes discrete and range ones
  const activeFacets = useActiveFacets({
    enrichedFacets,
    searchParams,
  });

  const onRemoveFilter = (facetId: string, filter: string) => {
    const existing = new Set(searchParams.getAll(facetId));
    existing.delete(filter);
    const next = new URLSearchParams(searchParams);
    next.delete(facetId);
    existing.forEach((v) => next.append(facetId, v));
    onUpdateActiveFilters(next);
  };

  const onRemoveRange = (facetId: string) => {
    const next = new URLSearchParams(searchParams);
    next.delete(`${facetId}.min`);
    next.delete(`${facetId}.max`);
    onUpdateActiveFilters(next);
  };

  const onRemoveCategory = (facetId: string) => {
    const next = new URLSearchParams(searchParams);
    next.delete(facetId);
    next.sort();
    onUpdateActiveFilters(next);
  };

  const onRemoveLocation = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('position.name');
    next.delete('position.location');
    next.delete('lat');
    next.delete('lon');
    onUpdateActiveFilters(next);
  };

  const onRemoveQ = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('q');
    onUpdateActiveFilters(next);
  };

  const onRemoveAllFilters = () => {
    onUpdateActiveFilters(new URLSearchParams());
  };

  // Wrapper function for the shared inventory status translator
  const translateInventoryStatus = useCallback(
    (facetId: string, status: string) => getTranslatedInventoryStatus(facetId, status, tInventory),
    [tInventory],
  );

  if (!activeFacets?.length && !q) return null;

  return (
    <div className={styles.wrapper}>
      <Suspense fallback={null}>
        <FilterModal
          isOpen={!!facet}
          facet={facet}
          rootUrl={rootUrl}
          onClose={() => setSelectedFacetId(undefined)}
          loading={loading}
          totalHits={totalHits}
        />
      </Suspense>
      <ul className={styles.list} aria-label={t('filters.ariaLabel')}>
        {/* Free-text q */}
        {q && (
          <li className={styles.item}>
            <Chip
              label={`"${q}"`}
              size="md"
              isActive={true}
              onRemove={() => onRemoveQ()}
              removeTitle={t('filters.removeFilter', {
                filter: `"${q}"`,
                category: t('filters.freeText'),
              })}
            />
          </li>
        )}

        {/* Active facets */}
        {activeFacets?.map((f) => {
          if (f.type === 'spatial') {
            const locationLabel = getLocationActiveFacetLabel(searchParams);
            return (
              <li className={styles.item} key={`location-${locationLabel}`}>
                <Chip
                  label={locationLabel}
                  size="md"
                  isActive={true}
                  title={t('filters.openFilterCategory', { category: f.displayName })}
                  onClick={() => setSelectedFacetId(f.id)}
                  onRemove={() => onRemoveLocation()}
                  removeTitle={t('filters.removeFilter', {
                    filter: locationLabel,
                    category: f.displayName,
                  })}
                />
              </li>
            );
          }
          if (f.type === 'toggle') {
            return (
              <li className={styles.item} key={`toggle-${f.id}-${f.displayName}`}>
                <Chip
                  label={f.displayName}
                  size="md"
                  isActive={true}
                  onRemove={() => onRemoveFilter(f.id, 'true')}
                  removeTitle={t('filters.removeFilter', {
                    filter: f.displayName,
                    category: f.displayName,
                  })}
                />
              </li>
            );
          }

          // Range facets
          if (f.type === 'range') {
            const rangeLabel = getRangeActiveFacetLabel({
              facet: f,
              searchParams,
              locale,
              market,
              currencySymbol: marketMetadata.currencySymbol,
              t,
            });

            return (
              <li className={styles.item} key={`${f.id}-${rangeLabel}`}>
                <Chip
                  label={rangeLabel}
                  size="md"
                  isActive={true}
                  title={t('filters.openFilterCategory', { category: f.displayName })}
                  onClick={() => setSelectedFacetId(f.id)}
                  onRemove={() => onRemoveRange(f.id)}
                  removeTitle={t('filters.removeFilter', {
                    filter: rangeLabel,
                    category: f.displayName,
                  })}
                />
              </li>
            );
          }

          // Discrete facets
          const filters = f.filters.filter((flt) =>
            searchParams.getAll(f.id).includes(flt.displayName),
          );
          if (filters.length === 1) {
            const flt = filters[0];
            const translatedDisplayName = translateInventoryStatus(f.id, flt.displayName);
            return (
              <li className={styles.item} key={`${f.id}-${flt.displayName}`}>
                <Chip
                  label={translatedDisplayName}
                  size="md"
                  isActive={true}
                  title={t('filters.openFilterCategory', { category: f.displayName })}
                  onClick={() => setSelectedFacetId(f.id)}
                  onRemove={() => onRemoveFilter(f.id, flt.displayName)}
                  removeTitle={t('filters.removeFilter', {
                    filter: translatedDisplayName,
                    category: f.displayName,
                  })}
                />
              </li>
            );
          }

          // Multiple discrete values
          return (
            <li className={styles.item} key={`${f.id}-group`}>
              <ul
                className={styles.filterGroup}
                aria-label={t('filters.filterCategoryGroup', { category: f.displayName })}
              >
                {filters.map((flt) => {
                  const translatedDisplayName = translateInventoryStatus(f.id, flt.displayName);
                  return (
                    <li className={styles.filterGroupItem} key={`${f.id}-${flt.displayName}`}>
                      <Chip
                        label={translatedDisplayName}
                        size="md"
                        isActive={true}
                        title={t('filters.openFilterCategory', { category: f.displayName })}
                        onClick={() => setSelectedFacetId(f.id)}
                        onRemove={() => onRemoveFilter(f.id, flt.displayName)}
                        removeTitle={t('filters.removeFilter', {
                          filter: translatedDisplayName,
                          category: f.displayName,
                        })}
                      />
                    </li>
                  );
                })}
                <li className={styles.filterGroupItem}>
                  <button
                    onClick={() => onRemoveCategory(f.id)}
                    title={t('filters.removeFilterCategory', { category: f.displayName })}
                    className={styles.filterGroupRemoveButton}
                  >
                    <X aria-hidden="true" />
                    <span className="sr-only">
                      {t('filters.removeFilterCategory', { category: f.displayName })}
                    </span>
                  </button>
                </li>
              </ul>
            </li>
          );
        })}
        <li className={clsx(styles.item, styles.itemClearButton)}>
          <button
            onClick={onRemoveAllFilters}
            title={t('filters.clear.title')}
            className={styles.clearButton}
          >
            {t('filters.clear.label')}
          </button>
        </li>
      </ul>
    </div>
  );
};

export default ActiveFilters;
